import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getGroupsState } from '../../../features/groups/selector';
import { getProjectUsersForDisplayState } from '../../../features/project/selectors';
import { Typography } from '@material-ui/core';
import { DocumentTemplateType, ICompany, IUser } from '../../../api-client/autogenerated';
import { getFilteredUsersFromUserGroup } from '../../../scripts/utils';
import { getCompaniesState } from '../../../features/companies/selector';
import { ManagePermissionsDialogType } from '../../design/ManagePermissionsDialog';
import _ from 'lodash';

type Props = {
  associatedUsers: string[];
  associatedGroups: string[];
  /**
   * Checks group-member permissions against this document type instead of whichever
   * document is currently on screen. Needed when previewing a notification list for a
   * document type other than the one currently being viewed — e.g. the Meeting Minutes
   * task review dialog previews Task/CA Task permissions while still on the Meeting
   * Minutes document.
   */
  targetDocumentType?: DocumentTemplateType;
};

interface CompanyWithUsers {
  company?: ICompany;
  users: IUser[];
}

export default function InlineNotificationList(props: Props) {
  const { associatedUsers, associatedGroups, targetDocumentType } = props;

  const groups = useSelector(getGroupsState);
  const companies = useSelector(getCompaniesState);
  // These users are already ON the notification list; this renders who is on
  // it. Resolved through the filtered selector a disabled user vanished from
  // the list entirely rather than being shown as a recipient.
  const projectUsers = useSelector(getProjectUsersForDisplayState);

  const notifiedUsers = useMemo<CompanyWithUsers[]>(() => {
    const groupUsers = associatedGroups.flatMap((groupId) => {
      const group = groups.find((g) => g.id === groupId);
      return getFilteredUsersFromUserGroup(
        ManagePermissionsDialogType.AssociatedUsers,
        group,
        targetDocumentType,
      );
    });

    const directUsers = associatedUsers.flatMap((userId) => {
      const user = projectUsers.find((pUser) => pUser.userId === userId)?.user;
      return user ? [user] : [];
    });

    const byCompany = new Map<string | undefined, CompanyWithUsers>();
    [...groupUsers, ...directUsers].forEach((user) => {
      const companyId = user.companyId ?? undefined;
      if (byCompany.has(companyId)) {
        byCompany.get(companyId)!.users.push(user);
      } else {
        byCompany.set(companyId, {
          company: companies.find((c) => c.id === companyId),
          users: [user],
        });
      }
    });

    return Array.from(byCompany.values());
  }, [associatedUsers, associatedGroups, groups, companies, projectUsers, targetDocumentType]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {notifiedUsers.map(({ company, users }, index) => (
        <div
          key={company?.id || index}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            marginBottom: '4px',
          }}
        >
          <Typography style={{ fontSize: 16, fontWeight: 700 }}>
            {company?.name || 'Unknown Company'}
          </Typography>
          {_.uniqBy(users, 'id').map((user) => (
            <Typography key={user.id} style={{ fontSize: 12 }}>
              {user.name}
            </Typography>
          ))}
        </div>
      ))}
    </div>
  );
}
